/**
 * UserSelect Component
 * A searchable dropdown for selecting users/lawyers (avocați)
 * Sorted alphabetically with search functionality
 */

import { useState, useMemo, useCallback } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

// Remove diacritics for search comparison
const removeDiacritics = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .replace(/[ăâ]/g, 'a')
    .replace(/[î]/g, 'i')
    .replace(/[ș]/g, 's')
    .replace(/[ț]/g, 't')
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

export function UserSelect({
  users = [],
  value,
  onValueChange,
  placeholder = "Selectează avocat...",
  emptyText = "Toți avocații",
  allowNone = true,
  disabled = false,
  className,
  "data-testid": testId = "user-select",
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter out colaborator_extern and sort users alphabetically by name
  const sortedUsers = useMemo(() => {
    return [...users]
      .filter(u => u.position !== "colaborator_extern")
      .sort((a, b) => {
        const nameA = removeDiacritics(a.name || "");
        const nameB = removeDiacritics(b.name || "");
        return nameA.localeCompare(nameB);
      });
  }, [users]);

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return sortedUsers;
    
    const searchNormalized = removeDiacritics(searchQuery.trim());
    
    return sortedUsers.filter(u => {
      const name = removeDiacritics(u.name || "");
      return name.includes(searchNormalized);
    });
  }, [sortedUsers, searchQuery]);

  // Get display text for selected value
  const selectedUser = useMemo(() => {
    if (!value || value === "none") return null;
    return users.find(u => u.id === value);
  }, [users, value]);

  const selectedDisplayText = useMemo(() => {
    if (!selectedUser) return emptyText;
    return selectedUser.name || "Avocat";
  }, [selectedUser, emptyText]);

  const handleSelect = useCallback((userId) => {
    onValueChange(userId === "none" ? "" : userId);
    setOpen(false);
    setSearchQuery("");
  }, [onValueChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", className)}
          data-testid={testId}
        >
          <span className="truncate text-left flex-1">
            {selectedUser ? (
              <span className="text-blue-600 font-medium">{selectedDisplayText}</span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start" data-user-select-popover>
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder="Caută după nume..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`${testId}-search`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="ml-2 rounded-sm opacity-70 hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <CommandList>
            <CommandEmpty>
              {searchQuery ? `Niciun avocat găsit pentru "${searchQuery}"` : "Nu există avocați"}
            </CommandEmpty>
            <CommandGroup>
              {allowNone && (
                <CommandItem
                  value="none"
                  onSelect={() => handleSelect("none")}
                  className="cursor-pointer [&[data-selected=true]]:bg-slate-100 [&[data-selected=true]]:text-slate-900"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      (!value || value === "none") ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="text-muted-foreground">{emptyText}</span>
                </CommandItem>
              )}
              {filteredUsers.map((user) => (
                <CommandItem
                  key={user.id}
                  value={user.id}
                  onSelect={() => handleSelect(user.id)}
                  className="cursor-pointer [&[data-selected=true]]:bg-slate-100 [&[data-selected=true]]:text-slate-900"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === user.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="flex flex-col">
                    <span className="text-blue-600 font-medium">{user.name}</span>
                    {user.position && (
                      <span className="text-xs text-muted-foreground">{user.position}</span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default UserSelect;
